function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useForm, FormProvider, useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { CloseButton, Divider, FullRow, GridItem, Row, TextButton, TextField, Typography, WithInfoIcon, useStateInRef } from 'seagull';
import { Close12 } from 'seagull/icons';
import { reviewerEmailValidator, reviewerNameValidator } from "../../../../business-logic/draftLogic";
import SignatoryCheckbox from "../SignatoryCheckbox/SignatoryCheckbox";
import { connectControllerToTextField } from "../../../../utils/react-hook-form.utils";
import styles from "./AddAdditionalRecipients.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function AdditionalRecipientRow(props) {
  const {
    idx,
    emailRules,
    disabled,
    trigger,
    onRemove,
    inBottomSheet
  } = props;
  const {
    control
  } = useFormContext();
  const firstNameField = /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".firstName"),
    rules: reviewerNameValidator,
    render: p => /*#__PURE__*/_jsx(TextField, _objectSpread({
      collapseHelperText: false,
      testid: "reviewer-".concat(idx, "-first-name"),
      label: "First name",
      disabled: disabled
    }, connectControllerToTextField(p)))
  });
  const lastNameField = /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".lastName"),
    rules: reviewerNameValidator,
    render: p => /*#__PURE__*/_jsx(TextField, _objectSpread({
      collapseHelperText: false,
      testid: "reviewer-".concat(idx, "-last-name"),
      label: "Last name",
      disabled: disabled
    }, connectControllerToTextField(p)))
  });
  const emailField = /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".email"),
    rules: emailRules,
    render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
      collapseHelperText: false,
      testid: "reviewer-".concat(idx, "-email"),
      label: "Email",
      disabled: disabled
    }, connectControllerToTextField(p)), {}, {
      onChange: e => {
        p.field.onChange(e);
        trigger('additionalRecipients');
      }
    }))
  });
  const signatoryCheckbox = /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "additionalRecipients.".concat(idx, ".isSignatory"),
    render: p => /*#__PURE__*/_jsx(SignatoryCheckbox, {
      disableInfoIcon: inBottomSheet,
      infoTestid: "reviewer-".concat(idx, "-required-sign-info"),
      info: "All signatures are required for the proposal to take effect.",
      disabled: disabled,
      checkboxTestid: "reviewer-".concat(idx, "-required-sign"),
      onChange: p.field.onChange,
      value: p.field.value
    })
  });
  return inBottomSheet ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Row, {
      alignItems: "center",
      gap: 8,
      children: [firstNameField, lastNameField]
    }), /*#__PURE__*/_jsx(FullRow, {
      children: emailField
    }), /*#__PURE__*/_jsxs(FullRow, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [signatoryCheckbox, !disabled && /*#__PURE__*/_jsx(TextButton, {
        underline: "none",
        "data-testid": "reviewer-".concat(idx, "-remove"),
        prefixIcon: /*#__PURE__*/_jsx(Close12, {}),
        onClick: onRemove,
        children: "Remove"
      })]
    }), /*#__PURE__*/_jsx(Divider, {
      margin: 0
    })]
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(FullRow, {
      alignItems: "center",
      gap: 8,
      children: [firstNameField, lastNameField, emailField, !disabled && /*#__PURE__*/_jsx(CloseButton, {
        skin: "transparent",
        "data-testid": "reviewer-".concat(idx, "-remove"),
        onClick: onRemove
      })]
    }), signatoryCheckbox]
  });
}
export default function AddAdditionalRecipients(props) {
  const {
    initialState,
    showReviewerErrors,
    primaryContact,
    amendmentInfoCallout,
    disabled,
    inBottomSheet
  } = props;
  const getProps = useStateInRef(props);
  const wasChangedRef = useRef(false);
  const isPrimaryASampleContact = primaryContact === null || primaryContact === void 0 ? void 0 : primaryContact.isSample();
  const form = useForm({
    mode: 'all',
    defaultValues: {
      additionalRecipients: initialState.map(r => ({
        firstName: r.firstName || '',
        lastName: r.lastName || '',
        email: r.email || '',
        isSignatory: r.isSignatory || false
      }))
    }
  });
  const recipientsControl = useFieldArray({
    control: form.control,
    name: 'additionalRecipients'
  });
  const recipients = form.watch('additionalRecipients');
  const hasRecipients = recipientsControl.fields.length > 0;
  // react-hook-form doesn't work immutably for an array when changing inputs, so we need to use the stringified version to compare changes.
  const stringifiedRecipients = JSON.stringify(form.watch('additionalRecipients'));
  const emailRules = useMemo(() => {
    const primaryAndRecipients = (primaryContact ? [{
      email: primaryContact.email()
    }] : []).concat(recipients);
    return reviewerEmailValidator(primaryAndRecipients);
  }, [primaryContact, recipients]);
  useEffect(() => {
    // We need to check for duplicate emails after removing a row, or changing primaryContact.
    form.trigger('additionalRecipients');
  }, [primaryContact, recipients.length]);
  useEffect(() => {
    if (form.formState.isDirty || wasChangedRef.current) {
      // react-hook-form doesn't work immutably for an array when changing inputs, so we need to clone the array.
      const newValue = [...form.getValues().additionalRecipients];
      getProps().onChange(newValue);
      wasChangedRef.current = true;
    }
  }, [stringifiedRecipients]);
  useEffect(() => {
    if (showReviewerErrors) {
      // Will auto-focus on the first invalid field.
      form.handleSubmit(() => {})();
    }
  }, [showReviewerErrors]);
  useEffect(() => {
    if (isPrimaryASampleContact) {
      const emptyReviewers = [];
      form.setValue('additionalRecipients', emptyReviewers);
      getProps().onChange(emptyReviewers);
    }
  }, [isPrimaryASampleContact]);
  const AddReviewer = /*#__PURE__*/_jsx(TextButton, {
    underline: "none",
    "data-testid": "add-reviewer",
    disabled: disabled,
    onClick: () => recipientsControl.append({
      firstName: '',
      lastName: '',
      email: '',
      isSignatory: false
    }),
    children: hasRecipients ? '+ Add an additional recipient' : '+ Add additional recipients'
  });
  const AdditionalRecipientsWithInfoIon = _ref => {
    let {
      children
    } = _ref;
    return /*#__PURE__*/_jsx(WithInfoIcon, {
      showInfo: !inBottomSheet,
      info: "Additional recipients get a link to view the proposal. They can add a payment method but don\u2019t need to approve it unless marked as 'required to sign'. They aren\u2019t added to the business but can be later by the primary.",
      children: children
    });
  };
  return /*#__PURE__*/_jsxs(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
    children: [/*#__PURE__*/_jsxs(GridItem, {
      className: cn(styles.root, {
        [styles.adjustUpwards]: !hasRecipients && !amendmentInfoCallout && !inBottomSheet,
        [styles.inBottomSheet]: inBottomSheet
      }),
      span: 12,
      children: [hasRecipients && /*#__PURE__*/_jsx(AdditionalRecipientsWithInfoIon, {
        children: /*#__PURE__*/_jsx(Typography, {
          weight: "normal",
          children: "Additional recipients"
        })
      }), recipientsControl.fields.map((r, idx) => /*#__PURE__*/_jsx(AdditionalRecipientRow, {
        inBottomSheet: inBottomSheet,
        idx: idx,
        setFocus: form.setFocus,
        trigger: form.trigger,
        emailRules: emailRules,
        disabled: disabled,
        onRemove: () => recipientsControl.remove(idx)
      }, r.id)), !isPrimaryASampleContact && recipientsControl.fields.length < 10 && (hasRecipients ? /*#__PURE__*/_jsx(_Fragment, {
        children: AddReviewer
      }) : /*#__PURE__*/_jsx(AdditionalRecipientsWithInfoIon, {
        children: AddReviewer
      })), amendmentInfoCallout]
    }), hasRecipients && !inBottomSheet && /*#__PURE__*/_jsx(GridItem, {
      className: styles.separator,
      span: 12
    })]
  }));
}